<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useActiveGame } from "../composables/useActiveGame.js";
import { useAuth } from "../composables/useAuth.js";
import { useAuthModal } from "../composables/useAuthModal.js";
import { useGameSettings } from "../composables/useGameSettings.js";
import { useCosmetics } from "../composables/useCosmetics.js";
import { useI18n } from "../composables/useI18n.js";
import type { GameStatusResponse, MultiplayerTournamentViewer } from "../../shared/api.js";
import type { MultiplayerTournamentState } from "../../core/multiplayerTournament.js";
import { apiGet, apiPost } from "../utils/api.js";
import AuthModal from "./AuthModal.vue";
import PlayerAvatar from "./PlayerAvatar.vue";

const router = useRouter();
const currentRoute = useRoute();

// A running game (online match, parked tournament match, or parked local
// game) the user has navigated away from — offered back via a prominent
// resume button.
const {
  activeOnlineGameId,
  activeOnlineGameWaiting,
  hasParkedTournamentMatch,
  parkedLocalGameRoute,
  setOnlineGame,
  clearOnlineGame,
} = useActiveGame();

/**
 * The stored online-game id is a claim, not a fact: the server deletes a
 * waiting game when its creator disconnects, and a match can finish while
 * this browser is closed. Left unchecked, the pill kept announcing "Waiting
 * for opponent" over a lobby that truthfully said "No open games right now".
 * So whenever the pill would show, its game is checked against the server
 * and cleared — or relabelled, waiting → resume — to match reality.
 */
async function reconcileActiveOnlineGame() {
  const id = activeOnlineGameId.value;
  if (!id) return;
  try {
    const status = await apiGet<GameStatusResponse>(
      `/api/games/${encodeURIComponent(id)}/status`,
    );
    if (activeOnlineGameId.value !== id) return; // moved on meanwhile
    if (
      !status.exists
      || status.status === "completed"
      || status.isPlayer === false
      || status.resumable === false
    ) {
      clearOnlineGame(id);
      return;
    }
    const waiting = status.status === "waiting";
    if (waiting !== activeOnlineGameWaiting.value) setOnlineGame(id, { waiting });
  } catch {
    // Offline or flaky network: keep the claim; the next pass rechecks.
  }
}
const resumeGameTarget = computed(() => {
  const onlineId = activeOnlineGameId.value;
  if (onlineId && currentRoute.path !== `/game/${onlineId}`) return `/game/${onlineId}`;
  if (hasParkedTournamentMatch.value) return "/local?tournament=1";
  // Hidden while on /local: a same-route push would not remount the page, and
  // the user is already in a local game there anyway.
  if (parkedLocalGameRoute.value && currentRoute.path !== "/local") return parkedLocalGameRoute.value;
  return null;
});

// A game you opened from the lobby that nobody has joined yet. Same pill, same
// place — it just says what is actually going on, and clicking it drops you
// into the game where the invite link lives.
const isWaitingForOpponent = computed(() => {
  const id = activeOnlineGameId.value;
  return activeOnlineGameWaiting.value && !!id && resumeGameTarget.value === `/game/${id}`;
});
const resumeGameLabel = computed(
  () => (isWaitingForOpponent.value ? t("Waiting for opponent") : t("Resume game")),
);

function resumeGame() {
  if (!resumeGameTarget.value) return;
  closeMobileMenu();
  router.push(resumeGameTarget.value);
}
const { user, guest, logout, loading: authLoading, fetchCurrentUser, error: authError } = useAuth();
const { settings } = useGameSettings();
const { t } = useI18n();
type TournamentEnvelope = {
  tournament: MultiplayerTournamentState;
  viewer: MultiplayerTournamentViewer;
};
/**
 * Running tournaments the signed-in user is in. They keep their own entries at
 * the top of the dropdown and put a dot on the parent link; the parent link
 * itself goes to the hub, which lists them again under "Continue". It used to
 * jump straight into the first one, which made the other formats reachable only
 * through the dropdown.
 */
const activeMultiplayerTournaments = ref<TournamentEnvelope[]>([]);
let activeTournamentPoll: ReturnType<typeof setInterval> | null = null;

async function refreshActiveMultiplayerTournaments() {
  const expectedUserId = user.value?.id;
  if (!expectedUserId) {
    activeMultiplayerTournaments.value = [];
    return;
  }
  try {
    const response = await apiGet<{ tournaments: TournamentEnvelope[] }>(
      "/api/multiplayer-tournaments?mine=active",
    );
    if (user.value?.id === expectedUserId) {
      // A 200 without the expected field would otherwise leave this undefined
      // and take the whole header down on the next render.
      activeMultiplayerTournaments.value = response.tournaments ?? [];
    }
  } catch {
    // Navigation remains usable even if this optional shortcut cannot refresh.
  }
}

// The wardrobe is fetched once for the whole app and refreshed when the signed
// -in user changes, so every page reads an already-warm cache.
const { state: cosmetics, refresh: refreshCosmetics } = useCosmetics();
watch(() => user.value?.id ?? null, () => void refreshCosmetics(), { immediate: true });

function toggleSound() {
  settings.soundEnabled = !settings.soundEnabled;
}

const { isOpen: showAuthModal, mode: authModalMode, open: openAuthModal, close: closeAuthModal } = useAuthModal();
const menuLoading = ref(false);
const menuError = ref("");
const isMobileMenuOpen = ref(false);
const mobileOpenGroup = ref<"play" | "tournament" | "puzzles" | "learn" | "community" | null>(null);
const notificationOpen = ref(false);
const notificationRoot = ref<HTMLElement | null>(null);
const isFullscreen = ref(false);

function openAuth(mode: "login" | "register") {
  openAuthModal(mode);
  closeMobileMenu();
}

function closeHeaderDropdownFocus() {
  const el = document.activeElement as HTMLElement | null;
  el?.blur();
}

function playSherlock(strength: "easy" | "medium" | "hard") {
  closeHeaderDropdownFocus();
  router.push(`/local?ai=sherlock&strength=${strength}`);
}

function playOffline() {
  closeHeaderDropdownFocus();
  router.push("/local?ai=off");
}

async function goToGame(gameId: string) {
  const target = `/game/${gameId}`;
  await router.push(target);
  await nextTick();
  if (router.currentRoute.value.fullPath !== target) {
    window.location.assign(target);
  }
}

async function createGame() {
  closeHeaderDropdownFocus();
  menuLoading.value = true;
  menuError.value = "";
  try {
    const data = await apiPost("/api/games", undefined, t("Failed to create game"));
    await goToGame(data.gameId as string);
  } catch (e: any) {
    menuError.value = e.message;
  } finally {
    menuLoading.value = false;
  }
}

async function jumpToOpenGames() {
  closeHeaderDropdownFocus();
  if (router.currentRoute.value.path !== "/") {
    await router.push({ path: "/", query: { openGames: "1" } });
    return;
  }
  document.getElementById("open-games-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

async function handleLogout() {
  await logout();
  activeMultiplayerTournaments.value = [];
  closeMobileMenu();
}

function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  if (!isMobileMenuOpen.value) mobileOpenGroup.value = null;
}

function closeMobileMenu() {
  isMobileMenuOpen.value = false;
  mobileOpenGroup.value = null;
}

function toggleMobileGroup(group: "play" | "tournament" | "puzzles" | "learn" | "community") {
  mobileOpenGroup.value = mobileOpenGroup.value === group ? null : group;
}

function playSherlockFromMobile(strength: "easy" | "medium" | "hard") {
  playSherlock(strength);
  closeMobileMenu();
}

function playOfflineFromMobile() {
  playOffline();
  closeMobileMenu();
}

async function createGameFromMobile() {
  await createGame();
  closeMobileMenu();
}

async function jumpToOpenGamesFromMobile() {
  await jumpToOpenGames();
  closeMobileMenu();
}

function onGlobalKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    closeMobileMenu();
    notificationOpen.value = false;
  }
}

// The tutorial tip counts as read once the panel has been opened; remembered
// across sessions so the red dot doesn't nag on every visit.
const TIP_SEEN_KEY = "rasenschach.tutorialTipSeen";
const tutorialTipSeen = ref(false);
try {
  tutorialTipSeen.value = localStorage.getItem(TIP_SEEN_KEY) === "1";
} catch { /* storage unavailable — dot simply stays */ }
const hasUnread = computed(() => !user.value && !tutorialTipSeen.value);

function toggleNotifications() {
  notificationOpen.value = !notificationOpen.value;
  if (notificationOpen.value && !tutorialTipSeen.value) {
    tutorialTipSeen.value = true;
    try {
      localStorage.setItem(TIP_SEEN_KEY, "1");
    } catch { /* storage unavailable — non-fatal */ }
  }
}

function closeNotifications() {
  notificationOpen.value = false;
}

async function goToInteractiveTutorial() {
  closeNotifications();
  await router.push("/learn/tutorial");
}

function onGlobalPointerDown(event: PointerEvent) {
  if (!notificationOpen.value) return;
  const target = event.target as Node | null;
  if (!target) return;
  if (notificationRoot.value?.contains(target)) return;
  notificationOpen.value = false;
}

function syncFullscreenState() {
  isFullscreen.value = !!document.fullscreenElement;
}

async function toggleFullscreen() {
  const docAny = document as Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
  };
  const rootAny = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
  };
  const fullscreenEl = document.fullscreenElement || docAny.webkitFullscreenElement;
  if (fullscreenEl) {
    if (document.exitFullscreen) await document.exitFullscreen();
    else if (docAny.webkitExitFullscreen) await docAny.webkitExitFullscreen();
    return;
  }
  if (document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen();
  else if (rootAny.webkitRequestFullscreen) await rootAny.webkitRequestFullscreen();
}

onMounted(async () => {
  await fetchCurrentUser();
  await refreshActiveMultiplayerTournaments();
  void reconcileActiveOnlineGame();
  activeTournamentPoll = setInterval(() => {
    void refreshActiveMultiplayerTournaments();
    void reconcileActiveOnlineGame();
  }, 10_000);
  syncFullscreenState();
  window.addEventListener("keydown", onGlobalKeydown);
  window.addEventListener("pointerdown", onGlobalPointerDown);
  document.addEventListener("fullscreenchange", syncFullscreenState);
  document.addEventListener("webkitfullscreenchange", syncFullscreenState as EventListener);
});

onUnmounted(() => {
  if (activeTournamentPoll) clearInterval(activeTournamentPoll);
  window.removeEventListener("keydown", onGlobalKeydown);
  window.removeEventListener("pointerdown", onGlobalPointerDown);
  document.removeEventListener("fullscreenchange", syncFullscreenState);
  document.removeEventListener("webkitfullscreenchange", syncFullscreenState as EventListener);
});

watch(
  () => router.currentRoute.value.fullPath,
  () => {
    closeMobileMenu();
    closeNotifications();
    void refreshActiveMultiplayerTournaments();
    void reconcileActiveOnlineGame();
  },
);

watch(user, () => {
  void refreshActiveMultiplayerTournaments();
});
</script>

<template>
  <button
    class="mobile-menu-toggle"
    :aria-label="t('Open navigation menu')"
    @click="toggleMobileMenu"
  >
    <span class="bar"></span>
    <span class="bar"></span>
    <span class="bar"></span>
  </button>

  <button
    v-if="resumeGameTarget"
    class="resume-btn mobile-resume-pill"
    :class="{ waiting: isWaitingForOpponent }"
    @click="resumeGame"
  >
    <span v-if="isWaitingForOpponent" class="resume-waiting-dot" aria-hidden="true"></span>
    <template v-else>▶ </template>{{ resumeGameLabel }}
  </button>

  <Teleport to="body">
    <div v-if="isMobileMenuOpen" class="mobile-menu-overlay" @click.self="closeMobileMenu">
      <aside class="mobile-menu-drawer" role="dialog" :aria-label="t('Mobile navigation')">
        <div class="mobile-menu-head">
          <router-link to="/" class="mobile-brand" @click="closeMobileMenu">
            Rasenschach
          </router-link>
          <button class="mobile-close" :aria-label="t('Close navigation menu')" @click="closeMobileMenu">&times;</button>
        </div>

        <router-link class="mobile-profile" to="/profile" @click="closeMobileMenu">
          <strong>{{ user?.displayName || guest?.name || t("Guest") }}</strong>
          <span v-if="guest">{{ t("ELO {elo} | {games} games", { elo: guest.elo, games: guest.gamesPlayed }) }}</span>
        </router-link>

        <button
          v-if="resumeGameTarget"
          class="resume-btn mobile-resume"
          :class="{ waiting: isWaitingForOpponent }"
          @click="resumeGame"
        >
          <span v-if="isWaitingForOpponent" class="resume-waiting-dot" aria-hidden="true"></span>
          <template v-else>▶ </template>{{ resumeGameLabel }}
        </button>

        <div class="mobile-group">
          <button class="mobile-group-toggle" :class="{ open: mobileOpenGroup === 'play' }" @click="toggleMobileGroup('play')">
            {{ t("Play") }}
          </button>
          <div v-if="mobileOpenGroup === 'play'" class="mobile-group-list">
            <button class="mobile-link-btn" @click="playSherlockFromMobile('easy')">{{ t("Sherlock AI — Easy") }}</button>
            <button class="mobile-link-btn" @click="playSherlockFromMobile('medium')">{{ t("Sherlock AI — Medium") }}</button>
            <button class="mobile-link-btn" @click="playSherlockFromMobile('hard')">{{ t("Sherlock AI — Hard") }}</button>
            <button class="mobile-link-btn" @click="playOfflineFromMobile">{{ t("Play offline") }}</button>
            <button class="mobile-link-btn" :disabled="menuLoading" @click="createGameFromMobile">{{ t("Create game") }}</button>
            <button class="mobile-link-btn" @click="jumpToOpenGamesFromMobile">{{ t("Join open game") }}</button>
          </div>
        </div>

        <div class="mobile-group">
          <button class="mobile-group-toggle" :class="{ open: mobileOpenGroup === 'tournament' }" @click="toggleMobileGroup('tournament')">
            {{ t("Tournaments") }}
          </button>
          <div v-if="mobileOpenGroup === 'tournament'" class="mobile-group-list">
            <router-link
              v-for="item in activeMultiplayerTournaments"
              :key="item.tournament.id"
              class="mobile-link active-tournament-link"
              :to="`/multiplayer-tournaments/${item.tournament.id}`"
              @click="closeMobileMenu"
            >&#9654; {{ item.tournament.name }}</router-link>
            <!-- The group header only expands the group, so the hub needs its
                 own row here or it is unreachable on a phone. -->
            <router-link class="mobile-link" to="/tournaments" @click="closeMobileMenu">{{ t("All tournaments") }}</router-link>
            <router-link class="mobile-link" to="/tournament" @click="closeMobileMenu">{{ t("My tournament") }}</router-link>
            <router-link class="mobile-link" to="/multiplayer-tournaments" @click="closeMobileMenu">{{ t("Multiplayer") }}</router-link>
            <router-link class="mobile-link" to="/tournament/history" @click="closeMobileMenu">{{ t("Show running/completed") }}</router-link>
          </div>
        </div>

        <div class="mobile-group">
          <button class="mobile-group-toggle" :class="{ open: mobileOpenGroup === 'puzzles' }" @click="toggleMobileGroup('puzzles')">
            {{ t("Puzzles") }}
          </button>
          <div v-if="mobileOpenGroup === 'puzzles'" class="mobile-group-list">
            <router-link class="mobile-link" to="/puzzles" @click="closeMobileMenu">{{ t("Puzzles") }}</router-link>
            <router-link class="mobile-link" to="/puzzles/create" @click="closeMobileMenu">{{ t("Create puzzles") }}</router-link>
          </div>
        </div>

        <div class="mobile-group">
          <button class="mobile-group-toggle" :class="{ open: mobileOpenGroup === 'learn' }" @click="toggleMobileGroup('learn')">
            {{ t("Learn") }}
          </button>
          <div v-if="mobileOpenGroup === 'learn'" class="mobile-group-list">
            <router-link class="mobile-link" to="/learn/tutorial" @click="closeMobileMenu">{{ t("Interactive tutorial") }}</router-link>
            <router-link class="mobile-link" to="/learn" @click="closeMobileMenu">{{ t("Rules") }}</router-link>
          </div>
        </div>

        <div class="mobile-group">
          <button class="mobile-group-toggle" :class="{ open: mobileOpenGroup === 'community' }" @click="toggleMobileGroup('community')">
            {{ t("Community") }}
          </button>
        <div v-if="mobileOpenGroup === 'community'" class="mobile-group-list">
          <router-link class="mobile-link" to="/community/players" @click="closeMobileMenu">{{ t("Players") }}</router-link>
          <router-link class="mobile-link" to="/community/forum" @click="closeMobileMenu">{{ t("Forum") }}</router-link>
            <router-link class="mobile-link" to="/about" @click="closeMobileMenu">{{ t("About") }}</router-link>
        </div>
      </div>

        <div class="mobile-actions">
          <button class="mobile-link-btn" @click="toggleSound">
            {{ settings.soundEnabled ? t("Sound: on") : t("Sound: off") }}
          </button>
          <router-link class="mobile-link" to="/settings" @click="closeMobileMenu">{{ t("Settings") }}</router-link>
          <router-link
            v-if="user?.isAdmin"
            class="mobile-link"
            to="/analytics"
            @click="closeMobileMenu"
          >
            {{ t("Analytics") }}
          </router-link>
          <button v-if="user" class="mobile-link-btn danger" :disabled="authLoading" @click="handleLogout">
            {{ authLoading ? t("Logging out...") : t("Log out") }}
          </button>
          <button v-else class="mobile-link-btn" @click="openAuth('register')">{{ t("Sign up") }}</button>
          <button v-if="!user" class="mobile-link-btn" @click="openAuth('login')">{{ t("Log in") }}</button>
        </div>
      </aside>
    </div>
  </Teleport>

  <header class="app-topbar">
    <div class="brand">
      <router-link to="/" class="brand-link">
        <img class="brand-logo" src="/logo-transparent.png" alt="" />
        <span class="brand-name">Rasenschach</span>
      </router-link>
    </div>

    <nav class="top-nav" :aria-label="t('Main navigation')">
      <div class="nav-group">
        <router-link class="nav-link" to="/">{{ t("Play") }}</router-link>
        <div class="dropdown play-menu">
          <div class="menu-section">
            <div class="menu-title">{{ t("Play against Sherlock AI") }}</div>
            <button class="menu-item" @click="playSherlock('easy')">{{ t("Easy") }}</button>
            <button class="menu-item" @click="playSherlock('medium')">{{ t("Medium") }}</button>
            <button class="menu-item" @click="playSherlock('hard')">{{ t("Hard") }}</button>
          </div>
          <div class="menu-section">
            <div class="menu-title">{{ t("More modes") }}</div>
            <button class="menu-item" @click="playOffline">{{ t("Play offline") }}</button>
            <button class="menu-item" :disabled="menuLoading" @click="createGame">{{ t("Create game") }}</button>
            <button class="menu-item" @click="jumpToOpenGames">{{ t("Join open game") }}</button>
          </div>
        </div>
      </div>

      <div class="nav-group">
        <router-link
          class="nav-link"
          :class="{ 'has-active-tournament': activeMultiplayerTournaments.length > 0 }"
          to="/tournaments"
        >{{ t("Tournaments") }}</router-link>
        <div class="dropdown">
          <router-link
            v-for="item in activeMultiplayerTournaments"
            :key="item.tournament.id"
            class="menu-item active-tournament-link"
            :to="`/multiplayer-tournaments/${item.tournament.id}`"
            @click="closeHeaderDropdownFocus"
          >&#9654; {{ item.tournament.name }}</router-link>
          <router-link class="menu-item" to="/tournaments" @click="closeHeaderDropdownFocus">{{ t("All tournaments") }}</router-link>
          <router-link class="menu-item" to="/tournament" @click="closeHeaderDropdownFocus">{{ t("My tournament") }}</router-link>
          <router-link class="menu-item" to="/multiplayer-tournaments" @click="closeHeaderDropdownFocus">{{ t("Multiplayer") }}</router-link>
          <router-link class="menu-item" to="/tournament/history" @click="closeHeaderDropdownFocus">{{ t("Show running/completed") }}</router-link>
        </div>
      </div>

      <div class="nav-group">
        <router-link class="nav-link" to="/puzzles">{{ t("Puzzles") }}</router-link>
        <div class="dropdown">
          <router-link class="menu-item" to="/puzzles" @click="closeHeaderDropdownFocus">{{ t("Puzzles") }}</router-link>
          <router-link class="menu-item" to="/puzzles/create" @click="closeHeaderDropdownFocus">{{ t("Create puzzles") }}</router-link>
        </div>
      </div>

      <div class="nav-group">
        <router-link class="nav-link" to="/learn/tutorial">{{ t("Learn") }}</router-link>
        <div class="dropdown">
          <router-link class="menu-item" to="/learn/tutorial" @click="closeHeaderDropdownFocus">{{ t("Interactive tutorial") }}</router-link>
          <router-link class="menu-item" to="/learn" @click="closeHeaderDropdownFocus">{{ t("Rules") }}</router-link>
        </div>
      </div>

      <div class="nav-group">
        <router-link class="nav-link" to="/community/players">{{ t("Community") }}</router-link>
        <div class="dropdown">
          <router-link class="menu-item" to="/community/players" @click="closeHeaderDropdownFocus">{{ t("Players") }}</router-link>
          <router-link class="menu-item" to="/community/forum" @click="closeHeaderDropdownFocus">{{ t("Forum") }}</router-link>
          <router-link v-if="cosmetics.enabled" class="menu-item" to="/shop" @click="closeHeaderDropdownFocus">{{ t("Club shop") }}</router-link>
          <router-link class="menu-item" to="/about" @click="closeHeaderDropdownFocus">{{ t("About") }}</router-link>
        </div>
      </div>
    </nav>

    <div class="account-area">
      <button
        v-if="resumeGameTarget"
        class="resume-btn"
        :class="{ waiting: isWaitingForOpponent }"
        @click="resumeGame"
      >
        <span v-if="isWaitingForOpponent" class="resume-waiting-dot" aria-hidden="true"></span>
        <template v-else>▶ </template>{{ resumeGameLabel }}
      </button>
      <router-link
        v-if="cosmetics.enabled && cosmetics.signedIn"
        class="coin-chip"
        to="/shop"
        :title="t('Club shop')"
      >
        <span class="coin-chip-icon" aria-hidden="true">◉</span>{{ cosmetics.coins }}
      </router-link>
      <router-link class="account-meta" to="/profile">
        <PlayerAvatar
          :avatar-url="user?.avatarDataUrl ?? null"
          :name="user?.displayName || guest?.name || t('Guest')"
          :size="30"
        />
        <div class="account-name-col">
          <div class="account-name-link">
            {{ user?.displayName || guest?.name || t("Guest") }}
          </div>
          <small v-if="guest" class="account-elo">{{ guest.elo }} ELO</small>
        </div>
      </router-link>
      <div class="account-links">
        <button
          class="icon-action-btn"
          :class="{ 'sound-off': !settings.soundEnabled }"
          type="button"
          :aria-label="settings.soundEnabled ? t('Turn sound off') : t('Turn sound on')"
          :title="settings.soundEnabled ? t('Sound: on') : t('Sound: off')"
          @click="toggleSound"
        >
          <svg v-if="settings.soundEnabled" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 9v6h4l5 5V4L7 9H3Zm13.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4ZM14 3.2v2.1a7 7 0 0 1 0 13.4v2.1a9 9 0 0 0 0-17.6Z"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 9v6h4l5 5V4L7 9H3Zm18.2 3 2-2-1.4-1.4-2 2-2-2L16.4 10l2 2-2 2 1.4 1.4 2-2 2 2 1.4-1.4-2-2Z"/>
          </svg>
        </button>
        <button
          class="icon-action-btn"
          type="button"
          :aria-label="isFullscreen ? t('Exit full screen') : t('Enter full screen')"
          :title="isFullscreen ? t('Exit full screen') : t('Full screen')"
          @click="toggleFullscreen"
        >
          <svg v-if="!isFullscreen" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 9V4h5v2H6v3H4Zm10-5h6v6h-2V6h-4V4ZM4 20v-6h2v4h4v2H4Zm14-2v-4h2v6h-6v-2h4Z"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 15H4v5h2v-3h3v-2Zm11 0h-5v2h3v3h2v-5ZM9 4H4v5h2V6h3V4Zm11 0h-5v2h3v3h2V4Z"/>
          </svg>
        </button>
        <router-link class="account-link" to="/settings">{{ t("Settings") }}</router-link>
        <router-link v-if="user?.isAdmin" class="account-link" to="/analytics">{{ t("Analytics") }}</router-link>
        <router-link v-if="user?.isAdmin" class="account-link" to="/admin">{{ t("Admin") }}</router-link>
        <button
          v-if="user"
          class="account-btn danger"
          :disabled="authLoading"
          @click="handleLogout"
        >
          {{ authLoading ? t("Logging out...") : t("Log out") }}
        </button>
        <button v-else class="account-btn" @click="openAuth('register')">{{ t("Sign up") }}</button>
        <button v-if="!user" class="account-btn muted" @click="openAuth('login')">{{ t("Log in") }}</button>
      </div>

      <div ref="notificationRoot" class="header-notification" :class="{ 'has-unread': hasUnread }">
        <div class="notification-trigger">
          <button
            class="notification-icon"
            type="button"
            :aria-label="t('Open notifications')"
            @click.stop="toggleNotifications"
          >
            <span v-if="hasUnread" class="notification-dot" aria-hidden="true"></span>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-4H5c0-1.2 1-1.6 1.8-2.3.7-.6 1.2-1.5 1.2-4.2 0-2.8 1.7-5 4-5.7V5a1 1 0 1 1 2 0v.8c2.3.7 4 2.9 4 5.7 0 2.7.5 3.6 1.2 4.2.8.7 1.8 1.1 1.8 2.3Z"
              />
            </svg>
          </button>
          <div v-if="notificationOpen" class="notification-panel" @pointerdown.stop>
            <button
              v-if="!user"
              type="button"
              class="notification-tip notification-tip-btn"
              @click="goToInteractiveTutorial"
            >
              {{ t("New here? Try out our") }} <span class="underline">{{ t("interactive tutorial") }}</span>.
            </button>
            <p v-else class="notification-empty">{{ t("No unread notifications.") }}</p>
          </div>
        </div>
      </div>
    </div>
  </header>

  <div v-if="menuError || authError" class="topbar-error">{{ menuError || authError }}</div>

  <AuthModal
    :is-open="showAuthModal"
    :initial-mode="authModalMode"
    @close="closeAuthModal"
    @success="closeAuthModal"
  />
</template>

<style scoped>
.mobile-menu-toggle,
.mobile-menu-overlay {
  display: none;
}

.app-topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 22px;
  min-height: 64px;
  padding: 0 22px;
  border-bottom: 1px solid var(--mobile-bar-border);
  background: linear-gradient(180deg, rgba(13, 19, 16, 0.98), rgba(10, 13, 11, 0.96));
  backdrop-filter: blur(8px);
}

.brand-link {
  margin: 0;
  line-height: 1;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 11px;
}

.brand-logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.brand-name {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 24px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--text-strong);
}

.top-nav {
  display: flex;
  align-items: center;
  gap: 2px;
}

.nav-group {
  position: relative;
}

.nav-link {
  display: block;
  padding: 9px 15px;
  text-align: center;
  color: var(--text-soft);
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.nav-link:hover {
  color: var(--text-strong);
  background: rgba(255, 255, 255, 0.06);
}

.nav-link.router-link-active {
  color: var(--accent-soft);
  background: rgba(40, 110, 70, 0.28);
}

.nav-link.has-active-tournament::after {
  content: "";
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-left: 7px;
  border-radius: 50%;
  background: var(--accent-strong);
  box-shadow: 0 0 8px rgba(52, 210, 123, 0.8);
}

.active-tournament-link {
  color: #8debb4 !important;
  background: rgba(52, 210, 123, 0.1) !important;
}

.dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 220px;
  border: 1px solid var(--border-strong);
  border-radius: 10px;
  background: var(--control-bg);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.45);
  padding: 8px;
  display: none;
  flex-direction: column;
  gap: 4px;
}

/* Invisible bridge across the 6px gap so hover survives the mouse travel. */
.dropdown::before {
  content: "";
  position: absolute;
  top: -8px;
  left: 0;
  right: 0;
  height: 8px;
}

.nav-group:hover > .dropdown,
.nav-group:focus-within > .dropdown {
  display: flex;
}

.play-menu {
  min-width: 340px;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.nav-group:hover > .play-menu,
.nav-group:focus-within > .play-menu {
  display: grid;
}

.menu-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.menu-title {
  margin: 2px 8px 4px;
  color: var(--text-faint);
  font-family: var(--font-mono);
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.menu-item {
  display: block;
  width: 100%;
  padding: 9px 10px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--text-body);
  text-decoration: none;
  text-align: left;
  cursor: pointer;
  font-size: 0.92rem;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.06);
  color: var(--text-strong);
}

.menu-item:disabled {
  opacity: 0.5;
  cursor: default;
}

.account-area {
  display: flex;
  align-items: center;
  gap: 14px;
  justify-self: end;
}

/* Coin balance beside the avatar; also the way into the shop. */
.coin-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border: 1px solid var(--gold-border);
  border-radius: 999px;
  background: rgba(244, 201, 93, 0.07);
  color: var(--gold-text);
  font-family: var(--font-mono);
  font-size: 0.82rem;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
}

.coin-chip:hover { background: rgba(244, 201, 93, 0.14); }
.coin-chip-icon { font-size: 0.9rem; }

/* Prominent pulsing pill offering the way back into a running game */
.resume-btn {
  border: 1px solid var(--accent-strong);
  border-radius: 999px;
  background: linear-gradient(180deg, #1f8a4d, var(--green));
  color: var(--text-on-accent);
  font-weight: 700;
  font-size: 0.9rem;
  padding: 7px 16px;
  cursor: pointer;
  white-space: nowrap;
  animation: resume-pulse 1.6s ease-in-out infinite;
}

.resume-btn:hover {
  background: linear-gradient(180deg, var(--green-hover), #1b7f47);
}

/* Waiting for somebody to accept: no longer an invitation to act, so it drops
   the loud green fill and becomes a quiet status chip that still leads there. */
.resume-btn.waiting {
  border-color: #2b5a3e;
  background: rgba(40, 110, 70, 0.18);
  color: var(--accent-soft);
  animation: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.resume-btn.waiting:hover {
  background: rgba(40, 110, 70, 0.3);
}

.resume-waiting-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #56d887;
  flex-shrink: 0;
  animation: resume-pulse 1.6s ease-in-out infinite;
}

@keyframes resume-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(52, 210, 123, 0.55); }
  50% { box-shadow: 0 0 0 7px rgba(52, 210, 123, 0); }
}

.mobile-resume {
  display: block;
  width: 100%;
  margin-bottom: 10px;
}

/* Desktop hides the floating pill — the top bar carries its own copy. Matched
   at .resume-btn.mobile-resume-pill on purpose: a bare .mobile-resume-pill
   would lose the cascade to `.resume-btn.waiting { display: inline-flex }`,
   which is exactly how the "Waiting for opponent" pill once escaped as a
   full-width banner above the desktop header. */
.resume-btn.mobile-resume-pill {
  display: none;
}

.header-notification {
  position: relative;
  display: flex;
  align-items: center;
}

.notification-icon {
  width: 22px;
  height: 22px;
  color: #e7ebf7;
  display: grid;
  place-items: center;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  position: relative;
}

.notification-icon:hover {
  color: #ffffff;
}

.notification-icon svg {
  width: 20px;
  height: 20px;
  fill: currentColor;
}

.notification-trigger {
  position: relative;
  display: flex;
  align-items: center;
}

.notification-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--danger);
  box-shadow: 0 0 0 2px rgba(14, 17, 24, 0.95);
}

.notification-panel {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  border: 1px solid var(--border-strong);
  border-radius: 10px;
  background: linear-gradient(180deg, #12271a, var(--control-bg));
  min-width: 270px;
  z-index: 5;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.35);
}

.notification-tip {
  display: block;
  border: 1px solid var(--border-accent);
  border-radius: 9px;
  background: transparent;
  color: var(--text-on-accent);
  text-decoration: none;
  font-size: 0.85rem;
  line-height: 1.2;
  font-weight: 600;
  padding: 10px 12px;
  margin: 0;
  white-space: nowrap;
}

.notification-tip-btn {
  width: 100%;
  text-align: left;
  cursor: pointer;
}

.notification-tip:hover {
  border-color: var(--accent-strong);
  background: linear-gradient(180deg, #17402a, #122c1e);
}

.notification-empty {
  margin: 0;
  padding: 10px 12px;
  color: var(--text-body);
  font-size: 0.84rem;
}

.account-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  border-radius: 8px;
  padding: 4px 6px;
}

.account-meta:hover {
  background: rgba(255, 255, 255, 0.06);
}

.account-name-col {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
  text-align: left;
  white-space: nowrap;
}

.account-name-link {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-strong);
  text-decoration: none;
}

.account-elo {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #7fbf9a;
}

.account-name-link:hover {
  text-decoration: none;
}

.account-links {
  display: flex;
  align-items: center;
  gap: 8px;
}

.icon-action-btn {
  width: 34px;
  height: 34px;
  border: 1px solid var(--border-soft);
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.02);
  color: var(--text-soft);
  display: grid;
  place-items: center;
  padding: 0;
  cursor: pointer;
  line-height: 1;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.icon-action-btn svg {
  width: 18px;
  height: 18px;
  fill: currentColor;
}

.icon-action-btn:hover {
  color: var(--text-strong);
  background: rgba(255, 255, 255, 0.06);
  border-color: var(--border-accent);
}

.icon-action-btn.sound-off {
  color: var(--text-faint);
}

.icon-action-btn.sound-off:hover {
  color: var(--text-soft);
}

.account-link {
  padding: 6px 10px;
  border-radius: 8px;
  color: var(--text-body);
  text-decoration: none;
  font-size: 0.88rem;
}

.account-link:hover {
  background: rgba(255, 255, 255, 0.06);
  color: var(--text-strong);
}

.account-btn {
  border: 1px solid #2d7a48;
  border-radius: 8px;
  background: linear-gradient(180deg, #1f8a4d, var(--green));
  color: var(--text-on-accent);
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease;
}

.account-btn:hover {
  background: var(--cta-gradient-hover);
}

.account-btn.danger {
  border-color: var(--danger-border);
  background: var(--danger-bg);
  color: var(--danger-text);
}

.account-btn.danger:hover {
  background: rgba(142, 52, 56, 0.34);
}

.account-btn.muted {
  border-color: var(--border-soft);
  background: rgba(255, 255, 255, 0.02);
  color: var(--text-body);
  font-weight: 600;
}

.account-btn.muted:hover {
  background: rgba(255, 255, 255, 0.06);
}

.topbar-error {
  margin: 8px auto 0;
  max-width: 1240px;
  color: var(--danger-text);
  background: rgba(185, 49, 55, 0.16);
  border: 1px solid #7f3338;
  border-radius: 8px;
  padding: 8px 12px;
}

@media (max-width: 1080px) {
  .app-topbar {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .account-area {
    justify-self: start;
    flex-wrap: wrap;
  }

  .account-meta {
    align-items: center;
  }

  .notification-tip {
    right: auto;
    left: 0;
    max-width: 280px;
    white-space: normal;
  }
}

@media (max-width: 980px) {
  .mobile-menu-toggle {
    display: grid;
    grid-template-rows: repeat(3, 2px);
    gap: 4px;
    position: fixed;
    top: 10px;
    left: 10px;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 1px solid var(--border-strong);
    background: rgba(13, 19, 16, 0.9);
    padding: 9px 8px;
    z-index: 70;
    cursor: pointer;
  }

  .mobile-menu-toggle .bar {
    display: block;
    width: 100%;
    height: 2px;
    border-radius: 999px;
    background: #dff2e6;
  }

  /* Floating pill next to the hamburger while the top bar is hidden */
  .resume-btn.mobile-resume-pill {
    display: block;
    position: fixed;
    top: 10px;
    left: 52px;
    z-index: 70;
    padding: 6px 14px;
    font-size: 0.85rem;
  }

  /* Waiting variant keeps the flex layout its status dot depends on. */
  .resume-btn.mobile-resume-pill.waiting {
    display: inline-flex;
  }

  .mobile-menu-overlay {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 80;
  }

  .mobile-menu-drawer {
    width: min(92vw, 340px);
    height: 100%;
    background: var(--control-bg);
    border-right: 1px solid var(--border);
    padding: 14px 12px 16px;
    box-sizing: border-box;
    overflow-y: auto;
  }

  .mobile-menu-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  .mobile-brand {
    font-family: var(--font-display);
    font-size: 1.3rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: var(--text-strong);
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .mobile-close {
    border: 1px solid var(--border-strong);
    background: rgba(255, 255, 255, 0.04);
    color: #dff2e6;
    border-radius: 6px;
    width: 28px;
    height: 28px;
    cursor: pointer;
  }

  .mobile-profile {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-decoration: none;
    color: var(--text-body);
    border: 1px solid var(--border-strong);
    border-radius: 8px;
    padding: 8px 10px;
    margin-bottom: 10px;
    background: rgba(255, 255, 255, 0.03);
  }

  .mobile-profile span {
    font-size: 0.8rem;
    font-family: var(--font-mono);
    color: #7fbf9a;
  }

  .mobile-group {
    margin-bottom: 8px;
  }

  .mobile-group-toggle {
    width: 100%;
    text-align: left;
    border: 1px solid var(--border-soft);
    background: rgba(255, 255, 255, 0.04);
    color: #dff2e6;
    font-family: var(--font-display);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-radius: 8px;
    padding: 9px 10px;
    font-size: 0.95rem;
    cursor: pointer;
  }

  .mobile-group-toggle.open {
    border-color: #2d7a48;
    background: rgba(40, 110, 70, 0.24);
    color: var(--accent-soft);
  }

  .mobile-group-link {
    display: block;
    box-sizing: border-box;
    text-decoration: none;
  }

  .mobile-group-list {
    margin-top: 6px;
    margin-left: 12px;
    padding-left: 10px;
    border-left: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .mobile-link,
  .mobile-link-btn {
    width: 100%;
    text-align: left;
    text-decoration: none;
    border: 1px solid var(--border-soft);
    background: rgba(255, 255, 255, 0.03);
    color: var(--text-body);
    border-radius: 7px;
    padding: 8px 10px;
    font-size: 0.88rem;
    cursor: pointer;
    box-sizing: border-box;
  }

  .mobile-link-btn:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .mobile-group-list .mobile-link,
  .mobile-group-list .mobile-link-btn {
    border: 1px solid var(--border);
    background: rgba(40, 110, 70, 0.12);
    color: var(--text-body);
    border-radius: 6px;
    padding: 7px 9px;
    font-size: 0.83rem;
  }

  .mobile-group-list .mobile-link:hover,
  .mobile-group-list .mobile-link-btn:hover:not(:disabled) {
    background: rgba(40, 110, 70, 0.24);
    border-color: var(--border-accent);
  }

  .mobile-link-btn.danger {
    border-color: #7f3338;
    background: rgba(142, 52, 56, 0.2);
  }

  .mobile-actions {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .app-topbar {
    display: none;
  }

  .topbar-error {
    margin-top: 52px;
    margin-left: 8px;
    margin-right: 8px;
  }
}
</style>
